<template>
  <AppTable :data="documents">
    <ElTableColumn prop="title" label="Title" width="30%" class-name="name-column">
      <template #default="{ row }">
        {{ row.title }}
      </template>
    </ElTableColumn>
    <ElTableColumn prop="file_type" label="Type" width="10%">
      <template #default="{ row }">
        <span class="file-type-badge">{{ row.file_type.toUpperCase() }}</span>
      </template>
    </ElTableColumn>
    <ElTableColumn prop="status" label="Status" width="10%">
      <template #default="{ row }">
        <span :class="['status-badge', `status-${row.status}`]">
          {{ statusLabel(row.status) }}
        </span>
      </template>
    </ElTableColumn>
    <ElTableColumn prop="chunk_count" label="Chunks" width="10%" sortable>
      <template #default="{ row }">
        {{ row.chunk_count }}
      </template>
    </ElTableColumn>
    <ElTableColumn prop="file_size" label="File size" width="10%">
      <template #default="{ row }">
        {{ formatFileSize(row.file_size) }}
      </template>
    </ElTableColumn>
    <ElTableColumn prop="created_at" label="Uploaded" width="15%" sortable>
      <template #default="{ row }">
        {{ formatHumanFriendlyDate(row.created_at) }}
      </template>
    </ElTableColumn>
    <ElTableColumn prop="actions" label="Actions" width="15%">
      <template #default="{ row }">
        <div class="d--f ai--c" style="gap: 8px">
          <a v-if="row.file_url" :href="row.file_url" download>
            <AppButton variant="outlined" size="small">
              <AppIcon color="primary" type="download" size="16" class="mr-5px" />
              Download
            </AppButton>
          </a>
          <AppButton variant="plain" size="small" @click="$emit('delete', row)">
            <AppIcon color="red" type="trash" size="16" />
          </AppButton>
        </div>
      </template>
    </ElTableColumn>
  </AppTable>
</template>

<script setup>
import { ElTableColumn } from "element-plus";
import { AppButton, AppIcon, AppTable } from "@/components/ui";
import { formatHumanFriendlyDate } from "@/utilities/date";

defineProps({
  documents: {
    type: Array,
    required: true,
  },
});

defineEmits(["delete"]);

const statusLabel = (status) => {
  const labels = { pending: "Pending", processing: "Processing", processed: "Ready", failed: "Failed" };
  return labels[status] || status;
};

const formatFileSize = (bytes) => {
  if (!bytes) return "N/A";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
</script>

<style lang="scss" scoped>
.file-type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: $color-grey-1;
  color: $color-grey-6;
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;

  &.status-pending {
    background: $color-grey-1;
    color: $color-grey-10;
  }

  &.status-processing {
    background: #d1ecf1;
    color: $color-blue-4;
  }

  &.status-processed {
    background: $color-green-1;
    color: $color-green;
  }

  &.status-failed {
    background: #f8d7da;
    color: $color-error;
  }
}

</style>
